// Message links (Copy message link, and their previews): a link to /s/<server>/<channel>/<message> on this app's own
// origin. Nothing about the linked message is stored with the message that holds the link: each viewer's app asks the
// room (messageRef), which answers only with messages that viewer can see, so a DM's words never reach anyone else.
import { createContext } from "react";
import { messageLinkPath, parseMessageLink, previewLinks, type MessageLinkRef } from "@bloom/shared";
import type { ChatMessage } from "../realtime.ts";

/** What the message list needs for message links, from the page. */
export interface MessageLinkHost {
  serverId: string;
  /** The channel or DM the list belongs to (a thread's: its channel): what Copy message link names. */
  rootId: string;
  /** The linked message and where it is; null when it's gone or not mine to see; undefined if the room didn't answer. */
  resolve: (messageId: string) => Promise<{ message: ChatMessage; rootId: string } | null | undefined>;
  /** Go to a linked message (another server's: opens that server at the link). */
  open: (ref: MessageLinkRef) => void;
}

/** Previews drawn under one message, at most. */
export const MAX_LINK_PREVIEWS = 3;

/** The full link to a message, for copying. */
export function messageLinkFor(host: MessageLinkHost, messageId: string): string {
  return location.origin + messageLinkPath({ serverId: host.serverId, channelId: host.rootId, messageId });
}

/**
 * The message links in `body` that get a preview: this server's, previewable (not in code, a spoiler or <…>), not
 * the message itself, at most MAX_LINK_PREVIEWS. Other servers' links stay plain links.
 */
export function linkPreviewsIn(body: string, host: MessageLinkHost, selfId: string): MessageLinkRef[] {
  if (!body.includes("/s/")) return [];
  const out: MessageLinkRef[] = [];
  for (const href of previewLinks(body, 10)) {
    const ref = parseMessageLink(href, location.origin);
    if (!ref || ref.serverId !== host.serverId || ref.messageId === selfId || out.some((r) => r.messageId === ref.messageId)) continue;
    out.push(ref);
    if (out.length >= MAX_LINK_PREVIEWS) break;
  }
  return out;
}

/**
 * How a message link reads once we know the message: "Maya in #general", "Maya in #general thread", "Maya in a DM".
 * `channelName`: the channel's name, or undefined for a DM.
 */
export function messageLinkLabel(author: string, channelName: string | undefined, inThread: boolean): string {
  return `${author} in ${channelName ? `#${channelName}` : "a DM"}${inThread ? " thread" : ""}`;
}

/** What a message link in message text needs to read as "Maya in #general": set by MessageList around its rows. */
export interface MessageLinkText {
  host: MessageLinkHost;
  names: Map<string, string>;
  channelName: (id: string) => string | undefined;
}
export const MessageLinkTextContext = createContext<MessageLinkText | null>(null);

/** The marker a pasted message link shows as in the message box (the real link goes back in when it's sent). */
export const PASTED_LINK_MARK = "🔗 ";

/** A message link pasted into the message box: shown as `label`, sent as `url`. */
export interface PastedLink {
  label: string;
  url: string;
  message: ChatMessage;
  rootId: string;
}

/**
 * The message box's text with each pasted link's label (still in it) put back as its link, first match first, and
 * the links still there. A label someone edited is just text now, and its link is dropped.
 */
export function expandPastedLinks(text: string, links: PastedLink[]): { text: string; kept: PastedLink[] } {
  let out = text;
  let from = 0;
  const kept: PastedLink[] = [];
  // In the order they appear, so two links with the same label each get theirs.
  const placed = links
    .map((l) => ({ l, at: text.indexOf(l.label) }))
    .filter((p) => p.at >= 0)
    .sort((a, b) => a.at - b.at);
  for (const { l } of placed) {
    const at = out.indexOf(l.label, from);
    if (at < 0) continue;
    out = out.slice(0, at) + l.url + out.slice(at + l.label.length);
    from = at + l.url.length;
    kept.push(l);
  }
  return { text: out, kept };
}
